// SubscriptionModal.jsx — upgrade dialog.
//
// Split card: a real Magi lesson playing on the left, the offer on the right. The plan is picked
// as a radio row rather than by choosing between two feature cards, which puts the actual charge
// ("Due now") on screen next to the button that authorises it.
//
// BILLING COPY IS LOAD-BEARING, NOT DECORATION. Auto-renewal rules (California ARL, FTC
// negative-option) require the real amount and the real frequency to be clear and next to the
// consent control. The previous version showed the monthly-equivalent price on the yearly tab
// and said "Renews automatically at $12.50/month" — wrong on both counts, since a yearly plan
// charges $149.99 once. Anything here that states a price or a period must come from
// `terms` below, which derives both from the selected period.
//
// House style: solid colours, no gradients, no spinners. The scrim blurs (as does the
// login gate's) — that is the overlay treatment; panels themselves stay solid.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Shield, ArrowRight, CreditCard, Check } from "lucide-react";
import WalletButtons from "./WalletButtons";
import { useCredits, DEFAULT_SUBSCRIPTION_PLANS } from "../hooks/useCredits";
import { useAuth } from "../AuthContext";
import { useT } from "../i18n/useT";
import { apiService } from "../apiService";
import { GENERIC_ERROR_MESSAGE, needsWithdrawalWaiver, withdrawalWindowDays } from "../utils/constants";

// Purpose-built showcase loop, not a frame lifted from a lesson.
//
// A lesson is deliberately sparse — one idea on screen at a time, which is good teaching but a
// poor window display. Measuring frame density across a real lesson showed one dense stretch in
// three minutes and blank either side, so every "pick a good timestamp" scheme was a lottery.
// These are written for the job instead: continuously busy, and authored to loop seamlessly.
// Source + render instructions: tools/showcase/showcase_clips.py (rendered on manimx, the same
// renderer production uses).
// Its own animation. Reusing a login-gate clip made the two surfaces look like the same
// screen shown twice, and this one has a different job: it is what the paid tier buys, so it
// is the only clip in the product that uses the 3D camera.
const SHOWCASE = "/showcase/subscribe.mp4";

const TERMS_URL = "https://www.iubenda.com/terms-and-conditions/52362461";
const PRIVACY_URL = "https://www.iubenda.com/privacy-policy/52362461";

// Mirrors settings.py, and is only a fallback — the live values come from /trial-config below
// so the disclosure can never state a price or duration Stripe will not actually charge.
// Mirrors the server constants (TRIAL_DAYS / TRIAL_PRICE_CENTS / TRIAL_VIDEO_CAP). `days`
// said 7 against the server's 14, so any render that beat /trial-config stated terms we do
// not sell — an auto-renewal disclosure being wrong is a compliance problem, not a typo.
const TRIAL_FALLBACK = { enabled: true, price_cents: 199, days: 14, video_cap: 10 };

const money = (cents) => {
  const v = cents / 100;
  return v % 1 === 0 ? `${v.toFixed(0)}` : v.toFixed(2);
};

// Defined at module scope ON PURPOSE. When this lived inside SubscriptionModal it was a fresh
// component type on every render, so React threw both rows away and rebuilt them on each state
// change. The click that set the state was consumed by that remount, which is why switching
// period took two clicks.
// Bolds every currency amount inside a disclosure string. Done with a regex rather than by
// splitting the sentence into fragments, because the wording is translated into 14 languages
// and clause order changes — the amounts are the one thing that looks the same in all of them.
const emphasiseAmounts = (text) =>
  String(text).split(/(\$[\d.,]+)/g).map((part, i) =>
    /^\$[\d.,]+$/.test(part)
      ? <strong key={i} className="text-white font-semibold">{part}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );

// The auto-renewal sentence for ONE period. Shared by `terms` below (the live copy) and by the
// invisible copies that reserve its height on phones, so the reservation can never be sized
// from different wording than the thing it is reserving for.
const renewalText = (period, plan, trial, t) => {
  if (period === "trial") {
    const renewOn = new Date(Date.now() + trial.days * 86400000);
    return t("home_sub_trial_terms", {
      trial: money(trial.price_cents),
      days: trial.days,
      n: trial.video_cap,
      date: renewOn.toLocaleDateString(undefined, { month: "long", day: "numeric" }),
      plan: money(plan.price_cents),
      planName: plan.name,
      full: plan.credits_per_month,
    });
  }
  if (period === "yearly" && plan.yearly_price_cents) {
    return t("billing_autorenew_yearly", { amount: money(plan.yearly_price_cents) });
  }
  return t("home_sub_monthly_terms", {
    amount: money(plan.price_cents),
    n: plan.credits_per_month,
    date: new Date(Date.now() + 30 * 86400000).toLocaleDateString(undefined, { month: "long", day: "numeric" }),
  });
};

const PeriodRow = ({ selected, onSelect, label, priceLabel, badge }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full flex items-center gap-3 px-4 py-2.5 lg:py-3 rounded-xl border-2 text-left transition-colors ${
      selected ? "border-[#635BFF] bg-[#635BFF]/10" : "border-[#2C2C31] bg-[#1A1A1D] hover:border-[#3A3A41]"
    }`}
  >
    <span
      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        selected ? "border-[#635BFF]" : "border-[#4A4A52]"
      }`}
    >
      {selected && <span className="w-2 h-2 rounded-full bg-[#635BFF]" />}
    </span>
    {/* ONE LINE PER SIDE, ON EVERY ROW. Monthly used to carry a "per month" caption under its
        price and the other two rows reserved a blank line to match its height — so one of the
        three said its unit and the other two held an empty line for it. Both are gone: the
        unit belongs to the headline price above, which already reads "$8.33 / month" with the
        real cadence under it, and every row here is a price for the period it names. With no
        second line to reserve, the rows are the same height by construction and the label and
        price actually sit on the row's centre line rather than above an empty one. */}
    <span className="flex-1 min-w-0 text-white font-semibold text-[15px]">{label}</span>
    {badge && (
      <span className="text-[11px] font-semibold text-green-400 bg-green-500/15 px-2 py-0.5 rounded-full whitespace-nowrap">
        {badge}
      </span>
    )}
    <span className="flex-shrink-0 text-white font-bold">{priceLabel}</span>
  </button>
);

/* `onSuccess` IS OPTIONAL AND HAS TO BE DECLARED ANYWAY. The wallet path below calls
   `onSuccess?.()` after an Apple/Google Pay charge clears, and it was never in this list — so
   on an UNDECLARED identifier the optional call does not save you: `?.()` guards a binding that
   is null or undefined, not one that does not exist, and the reference throws first. The one
   moment it fired was the moment a payment had just succeeded. None of the four call sites
   passes it today, which is why nobody saw it; declaring it makes the guard mean what it says
   and leaves a working hook for a caller that wants to react to a completed purchase. */
const SubscriptionModal = ({ onClose, onSuccess, user }) => {
  const { userData } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const { createSubscription, getSubscriptionPlans, manageSubscription, trialEligible } =
    useCredits(user?.uid, userData);

  /* WHAT THE BUYER IS ALREADY PAYING FOR.
   *
   *  This modal had no idea. It showed both tiers with live "Get started" buttons to everyone,
   *  so somebody already on Student saw exactly what a free user sees and could start a second
   *  checkout for the plan they are on. `/credits` — the older page this one replaced — has
   *  always read the same field and disabled the matching card, which is how the gap was
   *  visible: two screens selling the same thing, one of them knowing who it was talking to.
   *
   *  SAME FIELD AND SAME TIER LIST as PurchaseCreditsPage, deliberately: two different answers
   *  to "is this person subscribed" is how one screen comes to disagree with the other. */
  const currentTier = userData?.video_subscription_tier;
  const isSubscribed = ["student", "premium", "pro", "power"].includes(currentTier);

  const [subscriptionPlans, setSubscriptionPlans] = useState(DEFAULT_SUBSCRIPTION_PLANS);
  const [subscribing, setSubscribing] = useState(false);
  // Student is the default; Super is reachable via the quiet link under the CTA.
  const [planId, setPlanId] = useState("student");
  // YEARLY is the default (2026-09-10). Annual is the single biggest lever in the pricing
  // model — it survives down to a 60% signup penalty and a 20% renewal rate and still wins —
  // but it must never be the ONLY option: forcing annual costs ~40% of signups, while
  // defaulting to it keeps them. Monthly stays one click away, directly below.
  // Was "trial", which is now off (see TRIAL_ENABLED in settings.py).
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [trial, setTrial] = useState(TRIAL_FALLBACK);
  /* HAS THE SERVER ANSWERED YET. Both of the facts the trial row depends on — whether one is
     sold at all, and whether THIS person may have one — arrive from requests, and the
     defaults above answer "yes" to both so the row can paint before either lands. Reported as
     the trial "showing up for a split second then disappearing": a subscriber saw a $1.99
     offer appear and get withdrawn. The fallback is also stale (7 days against the server's
     14), so the flash stated terms we do not sell. The row now waits. */
  const [trialAnswered, setTrialAnswered] = useState(false);
  const [plansAnswered, setPlansAnswered] = useState(false);
  /* What the server did with a plan change — `{effective, effective_at,
     amount_charged_cents}`. Null until one happens. */
  const [changeResult, setChangeResult] = useState(null);

  useEffect(() => {
    getSubscriptionPlans()
      .then((plans) => {
        if (plans && Object.keys(plans).length > 0) setSubscriptionPlans(plans);
      })
      .catch(() => {})
      .finally(() => setPlansAnswered(true));   // `trialEligible` rides on this response
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiService.getTrialConfig()
      .then((c) => { if (c) setTrial(c); })
      .catch(() => {})
      .finally(() => setTrialAnswered(true));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* IS THE 14 DAYS ACTUALLY ON OFFER. Two independent answers, and both must be yes:
     `trial.enabled` is whether the product sells one at all (/trial-config), `trialEligible`
     is whether THIS person may have it — once you have been on any plan it is never offered
     to that address again. Derived rather than folded into `trial`, because the two arrive
     from separate requests and whichever landed second would overwrite the other. */
  const trialOffered =
    // Known locally and instantly: a subscriber is never eligible, and `userData` is already
    // in hand when the modal opens — so the commonest case needs no request and cannot flash.
    !isSubscribed
    // And never before both answers are in, so an offer is not shown and then taken away.
    && plansAnswered && trialAnswered
    && trial.enabled && trialEligible;

  const plan = subscriptionPlans[planId] || DEFAULT_SUBSCRIPTION_PLANS.student;
  // Declared down here, not beside `currentTier`: `planId` is a useState below that point and
  // reading it earlier is a temporal-dead-zone ReferenceError, not a stale value.
  const onThisPlan = isSubscribed && currentTier === planId;

  /* OPEN ON THE PLAN THEY HOLD. Without this the modal always opens on Student, so somebody
     on Super sees a live "Get started" for the cheaper tier — a checkout that DOWNGRADES
     them, one click from a paywall they did not need to see. Once, and only while they have
     not touched the switch themselves: after that the choice is theirs. `userData` arrives
     async, so this cannot be the useState initialiser — it would read null on first render. */
  const openedOnTier = React.useRef(false);
  useEffect(() => {
    if (openedOnTier.current || !isSubscribed) return;
    if (subscriptionPlans[currentTier]) {
      setPlanId(currentTier);
      openedOnTier.current = true;
    }
  }, [currentTier, isSubscribed, subscriptionPlans]);
  // Super (plan id "premium") is offered from 2026-09-10, but DELIBERATELY QUIETLY: a plain
  // grey text link under the CTA, not a second card and not a row in the period list. The
  // paywall keeps ONE decision axis — period — and the tier is a way out for the minority who
  // want more, not a choice everyone is made to weigh. Two tiers x two periods is a 2x2 matrix
  // at the exact moment someone is deciding whether to pay, and our own data says that decision
  // is made in a single session: 12 of our 14 subscribers made every free lesson in one day.
  const PLAN_SWITCH_ENABLED = true;
  const otherPlan = PLAN_SWITCH_ENABLED
    ? subscriptionPlans[planId === "premium" ? "student" : "premium"]
    : null;

  /**
   * Single source of truth for every price and period shown.
   *
   * `perMonth` is a comparison figure only. `dueNow` and `renewal` are what the customer is
   * actually charged, and they are the ones that must appear beside the button.
   */
  const terms = useMemo(() => {
    const monthlyCents = plan.price_cents;
    const yearlyCents = plan.yearly_price_cents;
    if (billingPeriod === "trial") {
      return {
        isTrial: true,
        perMonth: null,
        // What Stripe charges TODAY. Phase 1 of the schedule, nothing else.
        dueNow: money(trial.price_cents),
        renewal: renewalText("trial", plan, trial, t),
        // Same shape as the other two periods: "billed $X <cadence>". It used to repeat the
        // row's own label ("14-day full access"), which said the period twice and said the
        // price nowhere.
        //
        // AND IT SAYS WHAT HAPPENS NEXT. "billed $1.99 once for 14 days" is true and reads as
        // a one-off purchase — "once" is doing the opposite of its job here, because the one
        // thing this charge is not is the end of it. The renewal price was only in the fine
        // print four paragraphs down, which is exactly where a surprise charge comes from.
        cadenceNote: t("home_sub_billed_trial", {
          amount: money(trial.price_cents),
          days: trial.days,
          renews: money(plan.price_cents),
        }),
      };
    }

    const isYearly = billingPeriod === "yearly" && !!yearlyCents;

    if (isYearly) {
      return {
        isYearly: true,
        perMonth: money(Math.round(yearlyCents / 12)),
        dueNow: money(yearlyCents),
        // The REAL charge: one payment a year, not a monthly one.
        renewal: renewalText("yearly", plan, trial, t),
        cadenceNote: t("home_sub_billed_once_yearly", { amount: money(yearlyCents) }),
      };
    }
    return {
      isYearly: false,
      perMonth: money(monthlyCents),
      dueNow: money(monthlyCents),
      renewal: renewalText("monthly", plan, trial, t),
      cadenceNote: t("home_sub_billed_monthly", { amount: money(monthlyCents) }),
    };
  }, [plan, billingPeriod, t, trial]);

  // Cadence lines for every reachable period, so the block above reserves the tallest and
  // toggling cannot collapse it. A non-breaking space keeps the line boxed when there is no
  // note to show.
  const cadenceVariants = useMemo(() => {
    const out = [t("home_sub_billed_monthly", { amount: money(plan.price_cents) })];
    if (plan.yearly_price_cents) {
      out.push(t("home_sub_billed_once_yearly", { amount: money(plan.yearly_price_cents) }));
    }
    if (trialOffered) {
      // Same interpolation as the live line, or the reserved height is measured from a
      // shorter string than the one that actually paints and the card jumps on toggle.
      out.push(t("home_sub_billed_trial", {
        amount: money(trial.price_cents),
        days: trial.days,
        renews: money(plan.price_cents),
      }));
    }
    return out;
  }, [plan, trial, t]);

  // Every reachable period's disclosure sentence. On a phone the card has no fixed height and
  // no spacer (both are lg:-only), so the disclosure growing from one line on yearly to four on
  // the trial resized the whole modal under the reader's finger. The copies below reserve the
  // tallest wording the same way cadenceVariants does — and only below lg, because on desktop
  // the spacer already owns the slack and reserving here would park a void under the short
  // variants instead.
  const renewalVariants = useMemo(() => {
    const out = [];
    if (plan.yearly_price_cents) out.push(renewalText("yearly", plan, trial, t));
    out.push(renewalText("monthly", plan, trial, t));
    if (trial.enabled) out.push(renewalText("trial", plan, trial, t));
    return out;
  }, [plan, trial, t]);

  const savingsPct = useMemo(() => {
    if (!plan.yearly_price_cents) return null;
    const pct = Math.round((1 - plan.yearly_price_cents / (plan.price_cents * 12)) * 100);
    return pct > 0 ? pct : null;
  }, [plan]);

  // LOCK THE PAGE BEHIND IT. The card is position:fixed, so without this the document keeps
  // scrolling under a payment dialog — grab anywhere outside the card and the catalogue moves.
  // Same shape as LoginGate's lock: desktop only, because the stacked mobile layout genuinely
  // needs to scroll, and BOTH <html> and <body> because either one alone still scrolls on iOS.
  // Cleanup restores rather than clears-to-default, so a modal opened over an already-locked
  // surface cannot unlock it on close.
  useEffect(() => {
    const html = document.documentElement.style.overflow;
    const body = document.body.style.overflow;
    const apply = () => {
      const lock = window.matchMedia("(min-width: 1024px)").matches;
      document.documentElement.style.overflow = lock ? "hidden" : html;
      document.body.style.overflow = lock ? "hidden" : body;
    };
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      document.documentElement.style.overflow = html;
      document.body.style.overflow = body;
    };
  }, []);

  const [walletError, setWalletError] = useState(null);
  // Unticked by default and required before either payment path unlocks. This is the consent
  // the T&C withdrawal exception depends on; without it the clause claims something untrue.
  const [waived, setWaived] = useState(false);
  // Only consumers who actually hold a waivable withdrawal right are asked to waive it.
  const waiverNeeded = needsWithdrawalWaiver(userData?.country_code);
  const canPay = waived || !waiverNeeded;

  const handleSubscribe = async () => {
    if (!user) {
      navigate("/");
      return;
    }
    setSubscribing(true);
    try {
      const res = await createSubscription(planId, billingPeriod, waived);
      // A subscriber changing plan never leaves the page — there is no hosted checkout to
      // redirect to — so the modal has to say what happened itself. Without this the button
      // simply stopped spinning and nothing visibly changed, which reads as a failure for a
      // deferred downgrade where no charge appears either.
      if (res?.changed) {
        // THE CHARGE NEEDED THE CARDHOLDER. An immediate upgrade is billed to the card on
        // file, and if the bank wants 3-D Secure the invoice comes back unpaid with a hosted
        // page to finish on. Without this the modal would cheerfully report "you're on Super
        // now" over a charge that never completed.
        if (res.invoice_url) {
          window.location.href = res.invoice_url;
          return;
        }
        setChangeResult(res);
        setSubscribing(false);
        onSuccess?.();
      }
    } catch (err) {
      setSubscribing(false);
      alert(GENERIC_ERROR_MESSAGE);
    }
  };



  const monthlyOnly = money(plan.price_cents);
  // Null when the plan has no yearly price, which is what gates the yearly row below.
  const yearlyOnly = plan.yearly_price_cents ? money(plan.yearly_price_cents) : null;
  const yearlyPerMonth = plan.yearly_price_cents ? money(Math.round(plan.yearly_price_cents / 12)) : null;
  // COMPUTED, NEVER STATED. This badge used to read "2 months free", which was true only while
  // the yearly was exactly ten times the monthly. It no longer is ($99.90 against $14.99/mo is
  // 6.66 months), and a fixed phrase on a billing surface goes silently false the moment either
  // number moves — which is exactly what happened. Deriving it from the two prices the card is
  // already showing means the claim cannot drift from them. Rounded DOWN so the saving shown is
  // never larger than the saving given.
  const yearlySavePct = plan.yearly_price_cents && plan.price_cents
    ? Math.floor((1 - plan.yearly_price_cents / (plan.price_cents * 12)) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* TEST 1 of 2 for the open-lag: blur removed, everything else untouched.
          backdrop-filter over a full-screen scrim makes the compositor re-blur everything
          behind it at DEVICE pixels — on Explore that is 155 images plus a playing hero
          video, re-blurred on every frame anything behind it moves. bg-black/70 alone is a
          plain solid fill and costs nothing. This is also the house style: no frosted glass.
          If the modal still crawls, the remaining suspect is the 2.5 MB preload="auto"
          showcase clip below decoding while the card animates in. */}
      <div className="absolute inset-0 bg-black/70 overlay-scrim-enter" onClick={onClose} />

      {/* PINNED TO THE TALLER STATE so switching period cannot shrink the card and move the
          buttons under the cursor. The 669px floor was measured against the TRIAL wording; with
          the trial off and a price headline plus feature list now filling the column, the
          content sets the height and the floor only guards the short-translation case. A min
          rather than a fixed height, so a longer translation grows the card instead of clipping. */}
      <div
        role="dialog"
        aria-modal="true"
        /* 92dvh, not 92vh. On iOS `vh` resolves against the LARGE viewport — the one with the
           browser toolbars hidden — so on an iPhone this card was sized to 784px while only
           ~745px was on screen, and the last line ("Secured by Stripe…") sat behind the
           toolbar with no way to reach it: the card scrolls internally, but its own box
           extended past the visible area. `dvh` is the height actually visible, toolbars and
           all, so the cap now means what it says. */
        /* A FIXED height on desktop, not a minimum. The three periods carry different amounts
           of copy — a one-line renewal note on yearly, four lines on the trial, a feature line
           that cannot be shown on the trial at all — and with an elastic card every one of
           those differences moved something. Pinning the height lets a single spacer in the
           column below soak up all of it in ONE place, so the rows, the divider and the
           buttons land on the same pixel whichever period is selected. 700 clears the tallest
           variant (the trial) with room to spare. */
        className="relative w-full rounded-3xl overflow-hidden max-h-[92dvh] overflow-y-auto lg:h-[700px] overlay-card-enter"
        style={{ maxWidth: 940, background: "#131313", border: "1px solid #232326" }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-gray-300 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 lg:h-full">
          {/* ── Showcase ─────────────────────────────────────────── */}
          <div className="hidden lg:block p-3">
            {/* FULL HEIGHT. The panel used to be a square with the feature list stacked
                underneath, which left the media occupying barely half the column. The clip is
                authored portrait for exactly this shape, so it fills the column on its own —
                the clip is the argument here, not a bullet list. */}
            <div className="relative h-full min-h-[560px] rounded-[18px] overflow-hidden" style={{ background: "#0A0A10" }}>
              {/* Authored to loop, so plain `loop` is enough — no seeking, no timestamp
                  window, no poster swap. The clip's background matches the card. */}
              <video
                src={SHOWCASE}
                muted
                autoPlay
                playsInline
                loop
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>

          {/* ── Offer ────────────────────────────────────────────── */}
          {/* py-5 and the tightened gaps below, because this column overflowed a 92dvh cap on a
              ~780px-tall window and clipped the "Secured by Stripe" line — a payment card that
              scrolls hides exactly the reassurance it ends on. Dropping the period rows' reserved
              second line (see PeriodRow) took ~45px off; the rest comes from rhythm, not content,
              so nothing here says less than it did. */}
          <div className="px-5 sm:px-8 py-4 lg:py-5 flex flex-col lg:h-full">
            {/* pr-12 clears the close button. The X is absolutely positioned in the modal's
                top-right corner, so it does not reserve any space — on a phone the headline
                is wide enough to run under it and the last word sat behind the button. */}
            <h2 className="text-white text-[1.35rem] lg:text-[1.6rem] font-bold tracking-tight leading-tight mb-1 lg:mb-1.5 pr-12 lg:pr-0">
              {t("home_sub_headline", { plan: plan.name })}
            </h2>
            {/* No subhead. It read "Higher-quality animations, faster generation, and more
                videos every month" — which the feature list below now says twice, in less
                space and with ticks. Two redundant lines is what tipped the card into
                scrolling once the price headline and features were added. The i18n key is left
                defined in all 15 locales but is now unreferenced — deleting it would mean a
                key removal across every locale file for no gain, and it is the obvious thing
                to reach for if a subhead is ever wanted back. */}

            {/* Headline price. `perMonth` is the COMPARISON figure — for yearly it is the
                monthly equivalent ($8.33), which is not what Stripe charges, so the real
                cadence is stated directly underneath and "Due now" below still carries the
                actual amount. Never show the equivalent without the cadence line: a previous
                version showed "$12.50/month" on the yearly tab and said it renewed monthly,
                which was wrong on both counts. */}
            <div className="mb-3 lg:mb-4">
              <div className="flex items-baseline gap-1.5">
                <span className="text-white text-[2.1rem] lg:text-[2.6rem] font-bold tracking-tight leading-none">
                  ${terms.perMonth || terms.dueNow}
                </span>
                {/* UNIT ONLY. "/ month" belongs beside a number; a phrase does not. The
                    trial's number is not a monthly price, so it gets no unit here and is
                    qualified on the line below instead — see the cadence block. */}
                {terms.perMonth && (
                  <span className="text-gray-400 text-sm">{t("home_sub_per_month")}</span>
                )}
              </div>
              {/* HEIGHT IS RESERVED WHETHER OR NOT THIS LINE HAS TEXT. All three periods now
                  carry the same shape of line — "billed $X <cadence>" — so the three read as
                  one set rather than one explained period and two bare numbers.
                  Rendering nothing (which monthly used to do) collapsed the line and shifted
                  every element below by
                  ~21px on each toggle — the periods, the features, the disclosure, "Due now"
                  and both buttons all jumped. Same one-grid-cell trick as the disclosure below:
                  the longest variant sits invisible and sets the height, the live one paints
                  over it. Reserving via the real strings rather than a hardcoded min-height
                  keeps it correct when a translation runs longer. */}
              {/* The line that qualifies the number, in one construction for all three:
                  yearly "billed $99.90 once a year", monthly "billed $14.99 every month",
                  trial "billed $1.99 once for 14 days". The amount here is the REAL charge for
                  the period, which is what makes it worth a line on monthly too — the "/ month"
                  beside the price states a rate, this states the transaction. */}
              <div className="grid mt-1.5">
                {cadenceVariants.map((text, i) => (
                  <p key={i} aria-hidden="true"
                     className="[grid-area:1/1] invisible text-[13px] leading-relaxed">{text}</p>
                ))}
                <p className="[grid-area:1/1] text-gray-400 text-[13px] leading-relaxed">
                  {terms.cadenceNote ? emphasiseAmounts(terms.cadenceNote) : "\u00A0"}
                </p>
              </div>
            </div>

            <div className="space-y-2.5 mb-4">
              {/* CHEAPEST FIRST: trial, then monthly, then yearly. The eye starts at the top
                  of a list, and the smallest number is the easiest yes — the $1.99 is the
                  lowest barrier to entry there is, and burying it under a $199.90 made the
                  first thing a hesitant buyer saw the largest commitment on offer. Yearly is
                  still PRESELECTED (see the billingPeriod useState) so the best-value option
                  is the one you get by doing nothing; order and default are separate levers
                  and only the order is changing here.

                  Same three rows for both tiers — the plan is chosen by the quiet link under
                  the CTA, so this stays ONE decision axis rather than a 2x2 of tier x period.
                  The trial row renders only when it is genuinely on offer: TRIAL_ENABLED from
                  /trial-config AND this email never having had one. */}
              {trialOffered && (
                <PeriodRow
                  selected={billingPeriod === "trial"}
                  onSelect={() => setBillingPeriod("trial")}
                  label={t("home_sub_trial_label", { days: trial.days })}
                  priceLabel={`$${money(trial.price_cents)}`}
                />
              )}
              <PeriodRow
                selected={billingPeriod === "monthly"}
                onSelect={() => setBillingPeriod("monthly")}
                label={t("home_sub_monthly")}
                priceLabel={`$${monthlyOnly}`}
              />
              {yearlyOnly && (
                <PeriodRow
                  selected={billingPeriod === "yearly"}
                  onSelect={() => setBillingPeriod("yearly")}
                  label={t("home_sub_yearly")}
                  priceLabel={`$${yearlyOnly}`}
                  // No sublabel: the cadence line directly under the headline price already
                  // says "billed $99.90 once a year", and repeating it here cost a line on the
                  // tallest row for no information.
                  // Hidden rather than shown as "Save 0%" if the yearly ever stops being a
                  // discount — an absent badge is honest, a zero badge is an anti-sell.
                  badge={yearlySavePct > 0 ? t("home_sub_yearly_save", { pct: yearlySavePct }) : null}
                />
              )}
            </div>

            {/* What the plan actually includes. The lesson count is interpolated from the plan
                rather than hardcoded — the old keys were literally named `..._videos_50` and
                went stale the moment the allowance changed. Everything here must be true of
                the plan as configured; the free-tier allowance is deliberately NOT listed
                because the client has no access to it and a guessed number in billing copy is
                a false claim. */}
            {/* Hidden on the trial. These describe the PLAN, and on the trial the plan is not
                what you are buying yet — "50 videos per month" sat directly above a disclosure
                saying "capped at 10 videos", which is a contradiction on a billing surface. The
                trial's own terms are fully stated in the disclosure below, so nothing is lost.
                It also removes three lines from the tallest variant of this card. */}
            {/* Absent on the trial, where "50 videos per month" would sit directly above a
                disclosure saying "capped at 10 videos".
                On the trial this is EMPTY BUT PRESENT on a phone, and gone on desktop. The
                reasons differ by width: on desktop the spacer reclaims the space, so unmounting
                is clean; on a phone there is nothing to reclaim it, so unmounting moved
                everything below it. `invisible lg:hidden` keeps the lines occupying space at
                phone widths and removes the block entirely at lg. */}
            <ul className={`space-y-1.5 mb-3 lg:mb-4 ${terms.isTrial ? "invisible lg:hidden" : ""}`}
                aria-hidden={terms.isTrial ? "true" : undefined}>
              {/* CREDITS ONLY. "Higher quality model" and "PDF & image extraction" are real
                  gates — mode_hd is in SUBSCRIBER_ONLY_MODES and pdf_router checks tier — but
                  they are not what anyone is deciding on. The choice being made here is how
                  many lessons you get, so that is the only thing the list claims. Fewer, truer
                  lines also stop this block competing with the disclosure below it. */}
              {[
                t("home_sub_feature_videos_n", { n: plan.credits_per_month }),
              ].map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-[13px] text-gray-300">
                  <Check className="w-4 h-4 text-[#635BFF] flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {/* Auto-renewal disclosure — real amount, real frequency, adjacent to the CTA.
                Its wording runs one line on yearly and four on the trial, and the two widths
                absorb that difference differently. ON DESKTOP it renders at whatever height it
                needs and the spacer below soaks up the rest in one place — reserving the tallest
                here instead parked a void under the short variants, which is why that was
                removed. ON A PHONE there is no fixed card height and no spacer, so the same
                elasticity resized the whole modal on every period tap; the lg:hidden copies
                below reserve the tallest wording at phone widths only. */}
            <div className="grid mb-3 lg:mb-4">
              {/* PHONES ONLY — `lg:hidden` takes these out of the grid entirely at lg, so the
                  cell collapses back to the live sentence and desktop behaves exactly as before
                  (the spacer below keeps owning the slack). Below lg they set the cell to the
                  tallest wording, which is what stops the card resizing when the period
                  changes. Invisible, not transparent: the box still has to occupy its lines. */}
              {renewalVariants.map((text, i) => (
                <p key={i} aria-hidden="true"
                   className="[grid-area:1/1] lg:hidden invisible text-[12px] leading-snug">{text}</p>
              ))}
              <p className="[grid-area:1/1] text-[12px] text-gray-300 leading-snug">
                {emphasiseAmounts(terms.renewal)}
              </p>
            </div>

            {/* THE ONLY ELASTIC THING IN THE COLUMN. Everything above it is top-aligned and
                everything below it is pinned to the bottom, so whatever a period does not need
                collects here — invisible, and in the same place every time. Desktop only: on a
                phone the card is content-height and scrolls. */}
            <div className="hidden lg:block flex-1 min-h-4" />

            <div className="flex items-baseline justify-between mb-3 lg:mb-4 pt-3 lg:pt-4 border-t border-[#232326]">
              <span className="text-white font-semibold">{t("home_sub_due_now")}</span>
              <span className="text-white text-2xl font-bold">${terms.dueNow}</span>
            </div>

            {/* WALLETS FIRST. Apple Pay / Google Pay / Link render inside Stripe's own iframe
                and hand back a token — no card detail reaches this page or our servers. Each
                button only appears if the visitor can actually use it, so this area is empty on
                a browser with no wallet, which is why the card button below is never hidden. */}
            {waiverNeeded && (
            <label className="flex items-start gap-2.5 mb-3 lg:mb-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={waived}
                onChange={(e) => { setWaived(e.target.checked); if (e.target.checked) setWalletError(null); }}
                className="mt-0.5 w-4 h-4 accent-[#635BFF] flex-shrink-0"
              />
              <span className="text-[12px] text-gray-400 leading-relaxed">{t("home_sub_waiver", { days: withdrawalWindowDays(userData?.country_code) })}</span>
            </label>
            )}

            {walletError && (
              <div className="mb-3 text-[13px] text-red-300 text-center">{walletError}</div>
            )}
            {/* Wallets are hidden rather than disabled until the box is ticked: Stripe renders
                them in its own iframe, so there is no reliable way to intercept a tap on one. */}
            {/* `onThisPlan` hides the wallets for the same reason it disables the card button:
                Apple Pay for a subscription you already hold is a second charge, and the
                wallet path does not go through Stripe's hosted page where that would show. */}
            <div className={`mb-3 ${canPay && !onThisPlan ? "" : "hidden"}`}>
              <WalletButtons
                userId={user?.uid}
                planId={planId}
                billingPeriod={billingPeriod}
                withdrawalWaiver={waived}
                amountCents={Math.round(Number(terms.dueNow) * 100)}
                onError={setWalletError}
                onSuccess={() => { setWalletError(null); onSuccess?.(); onClose?.(); }}
              />
            </div>

            {/* The card path is deliberately unchanged: it still hands off to Stripe's hosted
                page, which is the flow that has been tested end to end. If anything on the
                wallet path fails, there is still a way to pay. */}
            {/* ALREADY ON THIS ONE -> NOTHING TO BUY. Disabled and grey, the same shape
                `/credits` has always used, and the same `pricing_your_plan` string so the two
                screens say it identically. The way out is the billing portal, not a second
                checkout for a plan you hold. */}
            {onThisPlan ? (
              <>
                <button
                  disabled
                  className="w-full py-3 lg:py-3.5 px-4 bg-white/10 text-gray-400 rounded-xl
                             font-semibold border border-white/15 cursor-default
                             flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {t("pricing_your_plan")}
                </button>
                {/* THE ERROR WAS BEING THROWN AWAY. `.catch(() => {})` meant every failure
                    here was invisible: the button appeared to do nothing at all. And it fails
                    reliably — this row shows when `video_subscription_tier` matches, but that
                    field is hand-editable and `scripts/set_plan.py` writes it with no Stripe
                    object behind it, so the portal lookup 400s. All eight non-self paid-tier
                    accounts are in exactly that state, so for every one of them the button
                    was dead and silent. BillingPage's twin has always alerted on failure;
                    this one was the odd surface out. */}
                <button
                  onClick={() => {
                    setWalletError(null);
                    manageSubscription().catch((err) => {
                      const msg = String(err?.message || "");
                      setWalletError(
                        /no active subscription/i.test(msg)
                          ? t("pricing_manage_unavailable")
                          : (msg || GENERIC_ERROR_MESSAGE)
                      );
                    });
                  }}
                  className="w-full text-center text-[13px] text-gray-500 hover:text-gray-300
                             transition-colors mt-2 lg:mt-3"
                >
                  {t("pricing_manage_cancel")}
                </button>
              </>
            ) : (
              <button
                onClick={() => (canPay ? handleSubscribe() : setWalletError(t("home_sub_waiver_required")))}
                disabled={subscribing}
                className="w-full py-3 lg:py-3.5 px-4 bg-white text-[#101014] rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {subscribing
                  ? t("home_sub_processing")
                  : (<><CreditCard className="w-4 h-4" />{t("home_sub_card")}</>)}
                {!subscribing && <ArrowRight className="w-4 h-4" />}
              </button>
            )}

            {/* SWITCHING CANCELS THE OLD ONE, SO SAY SO. A subscriber who picks the other tier
                gets a live CTA — correct, that is how you change plans — but what happens to
                the plan they hold is not obvious from a button that says "Pay with card".
                The server now ends the previous subscription once this purchase clears and
                credits the unused remainder; this is that promise, in front of the click. */}
            {changeResult ? (
              <p className="text-center text-[12px] text-emerald-300/90 leading-relaxed mt-2 lg:mt-3">
                {changeResult.effective === "now"
                  ? t("home_sub_change_now", {
                      plan: plan.name,
                      amount: `$${money(changeResult.amount_charged_cents || 0)}`,
                    })
                  : t("home_sub_change_later", {
                      plan: plan.name,
                      date: changeResult.effective_at
                        ? new Date(changeResult.effective_at).toLocaleDateString(undefined, {
                            month: "long", day: "numeric",
                          })
                        : "",
                    })}
              </p>
            ) : isSubscribed && !onThisPlan && (
              <p className="text-center text-[11px] text-gray-400 leading-relaxed mt-2 lg:mt-3">
                {t("home_sub_replaces_plan", {
                  plan: subscriptionPlans[currentTier]?.name || currentTier,
                })}
              </p>
            )}

            {otherPlan && (
              <button
                onClick={() => setPlanId(otherPlan.id)}
                className="w-full text-center text-[13px] text-gray-500 hover:text-gray-300 transition-colors mt-2 lg:mt-3"
              >
                {t("home_sub_switch_plan", { plan: otherPlan.name })}
              </button>
            )}

            <p className="text-center text-[11px] text-gray-400 leading-relaxed mt-2 lg:mt-3">
              {t("home_sub_agree_prefix")}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer"
                 className="text-gray-200 underline underline-offset-2 hover:text-white">{t("auth_terms")}</a>
              {t("auth_and")}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer"
                 className="text-gray-200 underline underline-offset-2 hover:text-white">{t("auth_privacy")}</a>.
            </p>

            <div className="flex items-center justify-center gap-1.5 text-[11px] text-gray-500 mt-3 lg:mt-4">
              <Shield className="w-3.5 h-3.5" />
              <span>{t("home_sub_secured")}</span>
              <span className="text-gray-700">·</span>
              <span>{t("home_sub_no_commitment")}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
